// components\ModalLed.tsx

import React, { useEffect, useState } from 'react';
import './ModalLed.css';
import MQTTService from './MQTTService';
import jwt_decode from 'jwt-decode';
import { fetchRecordsByEmail } from './DataHandler';

interface DeviceRecord {
  mac: string;
  name?: string[];
  in?: number[];
  out?: number[];
}

interface ModalProps {
  onCancel: () => void;
  labelNo: number;      // LED 번호
  deviceMac: string;    // 현재 장치 MAC
  name: string[];       // name 배열
  mqttMessage: string;  // 수신 MQTT 메시지(JSON string)
}

const ModalLed = ({ onCancel, labelNo, deviceMac, name, mqttMessage }: ModalProps) => {
  const [records, setRecords] = useState<DeviceRecord[]>([]);
  const [selectedTrigerState, setSelectedTrigerState] = useState<boolean | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<DeviceRecord | null>(null);
  const [selectedPort, setSelectedPort] = useState<number | null>(null);
  const [selectedState, setSelectedState] = useState<boolean | null>(null);
  const [delay, setDelay] = useState<number | ''>(''); // delay는 초 단위
  const [email, setEmail] = useState<string | null>(null);

  // ✅ 여러 기기/포트의 list 결과를 누적해서 보여주기 위한 맵
  //    key: `${mac}-${portNo}`, value: 해당 리스트 배열
  const [portStateMap, setPortStateMap] = useState<Record<string, any[]>>({});

  // 라벨(LED) 바꿔 열 때 입력폼 리셋 (리스트는 유지)
  useEffect(() => {
    setSelectedTrigerState(null);
    setSelectedDevice(null);
    setSelectedPort(null);
    setSelectedState(null);
    setDelay('');
  }, [labelNo]);

  // MQTT list 응답 누적 처리 (수정 버전)
  useEffect(() => {
    if (!mqttMessage) return;
    try {
      const msg = JSON.parse(mqttMessage);

      // list 응답 여부 (full or compressed)
      const isList = msg.operation === 'list' || msg.o === 'l';
      const sameMac = msg.mac === deviceMac || msg.m === deviceMac;
      if (!isList || !sameMac) return;

      const key = `${deviceMac}-${labelNo}`;

      // full: portState / compressed: ps
      const items = msg.portState || msg.ps || [];

      // 표준화
      const normalized = items.map((it: any) => ({
        trigger: it.trigger ?? it.t ?? false,
        delay: it.delay ?? it.du ?? 0,
        mac: it.mac ?? it.m ?? deviceMac,
        portNo: it.portNo ?? it.n ?? 0,
        value: it.value ?? it.v ?? false,
        slotIndex: it.slotIndex ?? it.si ?? 0,
      }));

      // 누적 추가 (보기 클릭 시 key는 이미 초기화됨)
      setPortStateMap(prev => ({
        ...prev,
        [key]: [...(prev[key] || []), ...normalized],
      }));
    } catch (error) {
      console.error('Error parsing MQTT message:', error);
    }
  }, [mqttMessage, deviceMac, labelNo]);


  // 토큰에서 이메일 추출
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decodedToken: any = jwt_decode(token);
        setEmail(decodedToken.email);
      } catch (error) {
        console.error('토큰 디코딩 오류:', error);
      }
    } else {
      console.error('토큰이 없습니다.');
    }
  }, []);

  // 이메일이 준비되면 현재 열려 있는 대상(맥/포트)의 리스트 요청
  useEffect(() => {
    if (email) {
      handleView();
    }
  }, [email, deviceMac, labelNo]);

  // 내 계정 기기 목록 로딩
  useEffect(() => {
    if (!email) return;
    fetchRecordsByEmail(email)
      .then((data: DeviceRecord[]) => setRecords(data))
      .catch((error) => console.error('기기 목록 가져오기 실패:', error));
  }, [email]);

  const handleInsert = () => {
    if (
      selectedDevice &&
      selectedPort !== null &&
      selectedTrigerState !== null &&
      selectedState !== null
    ) {
      const sendMessage = JSON.stringify({
        command: 'bindIO',
        operation: 'insert',
        trigger: selectedTrigerState, // ✅ 트리거 상태
        mac: deviceMac,               // ✅ 현재 장치 MAC
        portNo: labelNo,              // ✅ 현재 LED 번호
        delay: delay || 0,            // ✅ 지연 시간(초)
        portState: [
          {
            mac: selectedDevice.mac,
            portNo: selectedPort,
            value: selectedState,     // ✅ 트리거 시 동작할 대상 값
          }
        ],
      });

      const topic = `i2r/${email}/in`;
      MQTTService.getInstance().publish(topic, sendMessage);
      console.log(`Published to ${topic}: ${sendMessage}`);
    }

    // ✅ 서버 반영 기다렸다가 리스트 재조회 (기존 데이터 누적 유지)
    setTimeout(() => {
      handleView();
    }, 1000);
  };

  const handleDelete = (slotIndex: number) => {
    const sendMessage = JSON.stringify({
      command: 'bindIO',
      operation: 'delete',
      mac: deviceMac,
      portNo: labelNo,
      slotIndex: slotIndex,
    });

    const topic = `i2r/${email}/in`;
    MQTTService.getInstance().publish(topic, sendMessage);
    console.log('*** delete message:', sendMessage);

    // ✅ 재조회 (누적 유지)
    setTimeout(() => {
      handleView();
    }, 1000);
  };

  const handleDeleteAll = () => {
    const sendMessage = JSON.stringify({
      command: 'bindIO',
      operation: 'deleteAll',
      mac: deviceMac,
      portNo: labelNo,
    });

    const topic = `i2r/${email}/in`;
    MQTTService.getInstance().publish(topic, sendMessage);

    // ✅ 현재 mac-portNo 키만 비우고, 다른 기기/포트는 유지
    const key = `${deviceMac}-${labelNo}`;
    setPortStateMap(prev => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });

    // ✅ 서버 재조회
    setTimeout(() => {
      handleView();
    }, 1000);
  };

 
  const handleView = () => {
    const key = `${deviceMac}-${labelNo}`;

    // ✅ 이번 조회 시작: 화면의 해당 리스트만 깨끗하게
    setPortStateMap(prev => ({ ...prev, [key]: [] }));

    const sendMessage = JSON.stringify({
      command: 'bindIO',
      operation: 'list',
      mac: deviceMac,
      portNo: labelNo,   // 보드가 무시하더라도 OK
    });

    const topic = `i2r/${email}/in`;
    MQTTService.getInstance().publish(topic, sendMessage);
  };

  return (
    <div className="modal">
      <h3>{name?.[labelNo + 1] || 'Unnamed'}</h3>

      {/* ================================================================== */}
      {/* ★ DISPLAY SECTION START: 기기/포트별 누적 리스트를 화면에 출력하는 영역 */}
      {Object.keys(portStateMap).length > 0 && (
        <>
          {Object.entries(portStateMap).map(([key, list]) => (
            <div key={key} style={{ marginTop: '20px' }}>
              <h4>{key} 설정된 동작 목록</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ borderBottom: '1px solid #ccc', padding: '8px' }}>Trigger</th>
                    <th style={{ borderBottom: '1px solid #ccc', padding: '8px' }}>Delay</th>
                    <th style={{ borderBottom: '1px solid #ccc', padding: '8px' }}>MAC</th>
                    <th style={{ borderBottom: '1px solid #ccc', padding: '8px' }}>Port</th>
                    <th style={{ borderBottom: '1px solid #ccc', padding: '8px' }}>Value</th>
                    <th style={{ borderBottom: '1px solid #ccc', padding: '8px' }}>SlotIndex</th>
                    <th style={{ borderBottom: '1px solid #ccc', padding: '8px' }}>삭제</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((item, idx) => (
                    <tr key={`${key}-${idx}`}>
                      <td style={{ borderBottom: '1px solid #eee', padding: '8px' }}>
                        {item.trigger ? 'On' : 'Off'}
                      </td>
                      <td style={{ borderBottom: '1px solid #eee', padding: '8px' }}>
                        {item.delay}s
                      </td>
                      <td style={{ borderBottom: '1px solid #eee', padding: '8px' }}>
                        {item.mac}
                      </td>
                      <td style={{ borderBottom: '1px solid #eee', padding: '8px' }}>
                        {item.portNo ?? item.n}
                      </td>
                      <td style={{ borderBottom: '1px solid #eee', padding: '8px' }}>
                        {item.value ? 'On' : 'Off'}
                      </td>
                      <td style={{ borderBottom: '1px solid #eee', padding: '8px' }}>
                        {item.slotIndex}
                      </td>
                      <td
                        style={{
                          borderBottom: '1px solid #eee',
                          padding: '8px',
                          textAlign: 'center',
                        }}
                      >
                        <button
                          onClick={() => handleDelete(item.slotIndex)}
                          style={{
                            backgroundColor: 'transparent',
                            border: 'none',
                            color: 'red',
                            cursor: 'pointer',
                            fontSize: '18px',
                          }}
                          title="삭제"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </>
      )}
      {/* ★ DISPLAY SECTION END */}
      {/* ================================================================== */}

      <div>
        <h4>포트 트리거</h4>
        {records.length > 0 ? (
          <div>
            {/* 먼저 상태 선택 */}
            <select
              value={selectedTrigerState !== null ? selectedTrigerState.toString() : ''}
              onChange={(e) => {
                setSelectedTrigerState(e.target.value === 'true');
                // 기존 값 초기화
                setSelectedDevice(null);
                setSelectedPort(null);
              }}
            >
              <option value="" disabled>
                선택포트의 트리거를 선택하세요
              </option>
              <option value="true">On</option>
              <option value="false">Off</option>
            </select>

            {/* 상태 선택 시 기기 선택 */}
            {selectedTrigerState !== null && (
              <select
                value={selectedDevice?.mac || ''}
                onChange={(e) => {
                  const device = records.find((r) => r.mac === e.target.value);
                  setSelectedDevice(device || null);
                  setSelectedPort(null);
                }}
              >
                <option value="">기기를 선택하세요</option>
                {records.map((record, index) => (
                  <option key={index} value={record.mac}>
                    {record.name?.[0] || 'Unknown Device'}
                  </option>
                ))}
              </select>
            )}

            {/* 기기 선택 시 포트 선택 */}
            {selectedDevice && selectedDevice.out && (
              <select
                value={selectedPort ?? ''}
                onChange={(e) => setSelectedPort(Number(e.target.value))}
              >
                <option value="" disabled>
                  포트를 선택하세요
                </option>
                {selectedDevice.out.map((_, index) => {
                  const ledCount = selectedDevice.in?.length || 0;
                  return (
                    <option key={index} value={index}>
                      {selectedDevice.name?.[index + 1 + ledCount] || `스위치 ${index + 1}`}
                    </option>
                  );
                })}
              </select>
            )}

            {/* 포트 선택 시 상태 선택 */}
            {selectedPort !== null && (
              <select
                value={selectedState !== null ? selectedState.toString() : ''}
                onChange={(e) => setSelectedState(e.target.value === 'true')}
              >
                <option value="" disabled>
                  상태를 선택하세요
                </option>
                <option value="true">On</option>
                <option value="false">Off</option>
              </select>
            )}

            {/* Delay 입력 */}
            {selectedPort !== null && (
              <input
                type="number"
                placeholder="지연 시간 (초)"
                value={delay}
                onChange={(e) => setDelay(e.target.value === '' ? '' : Number(e.target.value))}
                style={{ marginTop: '10px' }}
              />
            )}
          </div>
        ) : (
          <p>등록된 기기가 없습니다.</p>
        )}
      </div>

      <div>
        <button onClick={handleView}>보기</button>
        <button onClick={handleInsert}>저장</button>
        <button onClick={handleDeleteAll}>모두지움</button>
        <button onClick={onCancel}>취소</button>
      </div>
    </div>
  );
};

export default ModalLed;
